import { useEffect, useRef, useState } from "react";

import { z } from "zod";

import {
  getJwtToken,
  getRefreshToken,
} from "@/components/session/JwtTokenManager";
import { useContainerDimensions } from "@/hooks/use-container-dimensions";

const imageAnalysisSchema = z.object({ description: z.string() });
type ImageAnalysis = z.infer<typeof imageAnalysisSchema>;

const PIXEL_DIFFERENCE_THRESHOLD = 0.05; // 5% of pixels changed
const COLOR_DIFFERENCE_THRESHOLD = 30; // RGB difference threshold (0-255)

/**
 * This is used for speeding up the image analysis by restricting the screenshot file size.
 * It should be tuned accordingly.
 *
 * The unit is in terms of pixels.
 */
const TARGET_SCREENSHOT_AREA = 480000;

export function useScreenShare(props: {
  containerAspectRatio?: number;
  onImageAnalzyed: (result: ImageAnalysis) => void;
}) {
  const container = useContainerDimensions();
  const [tick, setTick] = useState(0);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const previousFrameRef = useRef<ImageData | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoTrack = stream?.getVideoTracks()[0];

  const startCapture = async () => {
    setStream(
      await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: "browser",
        },
        audio: false,
      }),
    );
  };

  const stopCapture = () => {
    if (stream) {
      videoTrack?.stop();
      setStream(null);

      // Clear the previous frame reference
      previousFrameRef.current = null;

      // Reset tick counter
      setTick(0);
    }
  };

  // Stop screen sharing when this hook is unmounted
  useEffect(() => stopCapture, [stream]);

  const getCurrentFrameData = (
    track: MediaStreamTrack,
    videoElement: HTMLVideoElement,
  ): ImageData | null => {
    if (!canvasRef.current) {
      canvasRef.current = document.createElement("canvas");
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    const { width, height } = track.getSettings();
    canvas.width = width || 100;
    canvas.height = height || 100;

    ctx.drawImage(videoElement, 0, 0);
    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  };

  const analyzeCurrentFrame = async () => {
    if (!videoTrack) {
      return;
    }

    const videoElement = document.getElementById(
      "stream-video",
    ) as HTMLVideoElement;
    const currentFrameData = getCurrentFrameData(videoTrack, videoElement);

    if (!currentFrameData) {
      return;
    }

    // Check if we have a previous frame to compare against
    if (previousFrameRef.current) {
      const pixelDifferenceRatio = calculatePixelDifference(
        currentFrameData,
        previousFrameRef.current,
      );

      // Only analyze if the difference is significant
      if (pixelDifferenceRatio < PIXEL_DIFFERENCE_THRESHOLD) {
        // console.log("No significant changes detected, skipping analysis");
        return;
      }

      console.log(
        `Pixel difference: ${(pixelDifferenceRatio * 100).toFixed(2)}%`,
      );
    }

    // Store current frame for next comparison
    previousFrameRef.current = currentFrameData;

    // Generate image file from canvas and analyze
    if (!canvasRef.current) {
      return;
    }
    const resizedCanvas = resizeCanvasByArea(
      canvasRef.current,
      TARGET_SCREENSHOT_AREA,
    );

    const image = await generateImageFromCanvas(resizedCanvas);
    return analyzeImage(image);
  };

  useEffect(() => {
    const videoElement = document.getElementById(
      "stream-video",
    ) as HTMLVideoElement;
    if (videoElement) {
      videoElement.srcObject = stream;
    }
  }, [stream]);

  useEffect(() => {
    if (videoTrack) {
      // Detect if user pressed on the native Stop Sharing button
      videoTrack.addEventListener("ended", () => {
        stopCapture();
      });
    }
  }, [videoTrack]);

  useEffect(() => {
    if (!stream) return;

    const intervalId = setInterval(() => {
      analyzeCurrentFrame().then((result) => {
        if (result) {
          props.onImageAnalzyed(result);
          setTick((tick) => tick + 1);
        }
      });
    }, 1000); // Check every second

    return () => clearInterval(intervalId);
  }, [stream, tick]);

  const videoAspectRatio = videoTrack?.getSettings().aspectRatio;

  const renderWidth = (() => {
    if (!props.containerAspectRatio || !videoAspectRatio) {
      return undefined;
    }
    return (
      (container.dimensions.width * videoAspectRatio) /
      props.containerAspectRatio
    );
  })();

  return {
    active: stream !== null,
    start: startCapture,
    stop: stopCapture,
    Video: !stream
      ? null
      : () => (
          <div
            ref={container.setContainerRef}
            className="grid w-full h-full justify-center items-center bg-black"
          >
            <video
              style={{ width: renderWidth }}
              autoPlay
              playsInline
              muted
              id="stream-video"
            />
          </div>
        ),
  };
}

const analyzeImage = (file: File): Promise<{ description: string }> => {
  const formData = new FormData();
  formData.append("file", file);
  return fetch(`${API_HOST}/analyze-image`, {
    method: "POST",
    body: formData,
    headers: {
      Authorization: `JWT ${getJwtToken()}`,
      Session: getRefreshToken() ?? "",
      IsExec: "true",
    },
  })
    .then((response) => response.json())
    .then((raw) => imageAnalysisSchema.parse(raw));
};

const generateImageFromCanvas = (canvas: HTMLCanvasElement): Promise<File> => {
  return new Promise<File>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], "screenshot.jpeg", {
            type: "image/jpeg",
          });
          resolve(file);
        } else {
          reject("Failed to convert blob");
        }
      },
      "image/jpeg",
      0.5,
    );
  });
};

const resizeCanvasByArea = (
  sourceCanvas: HTMLCanvasElement,
  targetArea: number = 480000,
): HTMLCanvasElement => {
  const resizedCanvas = document.createElement("canvas");
  const ctx = resizedCanvas.getContext("2d");
  if (!ctx) return sourceCanvas;

  const { width, height } = sourceCanvas;
  const currentArea = width * height;

  // If current area is already smaller than target, return original
  if (currentArea <= targetArea) {
    return sourceCanvas;
  }

  // Calculate scaling factor based on area ratio
  const scaleFactor = Math.sqrt(targetArea / currentArea);

  const newWidth = Math.floor(width * scaleFactor);
  const newHeight = Math.floor(height * scaleFactor);

  resizedCanvas.width = newWidth;
  resizedCanvas.height = newHeight;

  ctx.drawImage(sourceCanvas, 0, 0, newWidth, newHeight);
  return resizedCanvas;
};

const calculatePixelDifference = (
  currentFrame: ImageData,
  previousFrame: ImageData,
): number => {
  const currentData = currentFrame.data;
  const previousData = previousFrame.data;

  if (currentData.length !== previousData.length) {
    return 1; // 100% different if dimensions changed
  }

  let changedPixels = 0;
  const totalPixels = currentData.length / 4; // RGBA = 4 values per pixel

  for (let i = 0; i < currentData.length; i += 4) {
    const rDiff = Math.abs(currentData[i] - previousData[i]);
    const gDiff = Math.abs(currentData[i + 1] - previousData[i + 1]);
    const bDiff = Math.abs(currentData[i + 2] - previousData[i + 2]);

    // Calculate the overall color difference for this pixel
    const colorDifference = Math.sqrt(
      rDiff * rDiff + gDiff * gDiff + bDiff * bDiff,
    );

    if (colorDifference > COLOR_DIFFERENCE_THRESHOLD) {
      changedPixels++;
    }
  }

  return changedPixels / totalPixels;
};
